import moment from "moment";
import {CreateSepaXmlByTimeRangeInput} from "../../../generated/graphql";

/**
 * Baut den `CreateSepaXmlByTimeRangeInput` für den SEPA-Lastschriftexport.
 *
 * Die DatePicker liefern Moment-Objekte in lokaler Zeit (Wanduhr-Zeit des Browsers,
 * i.d.R. Europe/Berlin). ACHTUNG: Die drei Datumsfelder haben UNTERSCHIEDLICHE Semantik
 * und müssen daher unterschiedlich serialisiert werden:
 *
 * 1) `startDate` / `endDate` sind reine FILTER-GRENZEN. Der Server vergleicht sie als
 *    Zeit-Instanz gegen `scheduledPayment.paymentDue` (`>= startDate`, `<= endDate`).
 *    → Wir berechnen `startOf/endOf("day")` in lokaler Zeit und serialisieren das Ergebnis
 *      als ISO-UTC-String. Dadurch entspricht die Grenze exakt dem lokalen Tagesanfang/-ende —
 *      genauso wie die Offene-Posten-Liste (`.endOf("day").toISOString()`).
 *    → KEIN `.utc(true)`: `moment.utc(keepLocalTime=true)` würde die Wanduhr-Anzeige beibehalten
 *      und nur das Label auf UTC umstellen, wodurch sich der tatsächliche Zeitpunkt um den lokalen
 *      UTC-Offset (Sommer +2 h, Winter +1 h) nach hinten verschiebt und der Export Lastschriften
 *      einen Tag zu weit in den Zeitraum zieht.
 *
 * 2) `collectionDate` ist das KALENDER-Datum des Lastschrift-Einzugs (Ausführungstag). Das
 *    `sepa`-Paket rendert es serverseitig als `collectionDate.toISOString().substr(0, 10)` in das
 *    XML-Feld `ReqdColltnDt` — es zählt also der UTC-Kalendertag, nicht die Uhrzeit/Instanz.
 *    → Hier ist `.utc(true)` ZWINGEND: lokale Mitternacht des gewählten Tages wird als UTC-Mitternacht
 *      desselben Kalendertages umgelabelt (z.B. 04.06. → `2026-06-04T00:00:00.000Z` → `ReqdColltnDt`
 *      = `2026-06-04`). Ohne `.utc(true)` würde `2026-06-03T22:00:00.000Z` entstehen → Einzug einen
 *      Tag zu früh.
 */

type DatePickerValue = moment.Moment | string | null | undefined;

export type BuildTimeRangeInputArgs = {
    startDate?: DatePickerValue;
    endDate?: DatePickerValue;
    collectionDate?: DatePickerValue;
    saveFile?: boolean;
    maxAmount?: number;
    maxMoneyAmount?: number;
};

export const buildTimeRangeInput = ({
    startDate,
    endDate,
    collectionDate,
    saveFile,
    maxAmount,
    maxMoneyAmount,
}: BuildTimeRangeInputArgs): CreateSepaXmlByTimeRangeInput => {
    return {
        // Filter-Grenzen: lokales Tagesende/-anfang als echter UTC-Instant (ohne .utc(true)).
        startDate: startDate ? moment(startDate).startOf("day").toISOString() : undefined,
        endDate: endDate ? moment(endDate).endOf("day").toISOString() : undefined,
        // Ausführungs-Kalendertag: UTC-Mitternacht des gewählten Tages (mit .utc(true)),
        // damit das XML-Feld ReqdColltnDt den korrekten Kalendertag enthält.
        collectionDate: collectionDate ? moment(collectionDate).startOf("day").utc(true).toISOString() : undefined,
        saveFile,
        maxAmount,
        maxMoneyAmount,
    };
};
